import { Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseInputDirective } from '../base-input/base-input.directive';

@Component({
    selector: 'lib-gung-number-input-horizontal',
    templateUrl: './gung-number-input-horizontal.component.html',
    styleUrls: ['./gung-number-input-horizontal.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => GungNumberInputHorizontalComponent),
            multi: true
        }
    ],
    standalone: false
})
export class GungNumberInputHorizontalComponent extends BaseInputDirective<number | string> implements OnInit {
  @Input()
  public labelClass: string | string[];

  @Input()
  public extraInputClass = '';

  @Input()
  public extraFormClass = '';

  @Input()
  protected outputAsString?: boolean;

  @Input()
  public labelBold = false;

  @Input()
  decimals?: number;

  @Input()
  stepAmount: number;

  @Input()
  minValue: number | string;

  @Input()
  maxValue: number | string;

  @Input()
  public footnote = false;

  @Output() blur = new EventEmitter<void>();

  @Output() input = new EventEmitter<Event>();

  @ViewChild('inputNumber', { static: true }) inputNumberElement: ElementRef;

  constructor(inputNumberElement: ElementRef) {
    super();
    this.inputNumberElement = inputNumberElement;
  }

  set value(value: number) {
    // Both sides of these comparisons are strings often enough that an untyped > compares them
    // lexicographically: the bounds arrive as strings whenever a template writes them as static
    // attributes (maxValue="100"), and value is a string whenever outputAsString is set. '5' >
    // '100' is true, which silently snapped a 5 % discount to 100 %. trello.com/c/IjcczDHw
    const numericValue = Number(value);
    const max = this.toBound(this.maxValue);
    const min = this.toBound(this.minValue);
    // A maximum of 0 has always meant "no maximum" to the callers, a minimum of 0 is a real bound.
    if (max !== undefined && max !== 0 && numericValue > max) {
      value = max;
      this.inputNumberElement.nativeElement.value = value;
    }
    if (min !== undefined && numericValue < min) {
      value = min;
      this.inputNumberElement.nativeElement.value = value;
    }
    if (this.outputAsString) {
      super.value = '' + this.fixDecimals(Number(value));
    } else {
      super.value = this.fixDecimals(value);
    }
    if (super.value !== value) {
      // Update INPUT value
      this.inputNumberElement.nativeElement.value = super.value;
    }
  }

  get value() {
    if (super.value === null || super.value === undefined || super.value === '') {
      return;
    }
    if (this.outputAsString) {
      return this.fixDecimals(Number(super.value));
    }
    return Number(this.fixDecimals(super.value));
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setStepAmount();
  }

  protected toBound(bound: number | string): number | undefined {
    if (bound === null || bound === undefined || bound === '') {
      return undefined;
    }
    const parsed = Number(bound);
    return isNaN(parsed) ? undefined : parsed;
  }

  fixDecimals(value): number {
    if (value && this.decimals !== null && this.decimals !== undefined) {
      value = +value.toFixed(this.decimals);
    }
    return value;
  }

  setStepAmount() {
    if (this.decimals !== null && this.decimals !== undefined && !this.stepAmount) {
      let stepAmount = '0.0000000001';
      stepAmount = stepAmount.substring(0, 2) + stepAmount.substring(stepAmount.length - this.decimals);
      this.stepAmount = +parseFloat(stepAmount);
    }
  }

  onBlur() {
    this.onTouched();
    this.blur.emit();
  }

  onInput(event) {
    const target = event.target as HTMLInputElement;
    const value = target.value ? Number(target.value) : null;
    this.value = value;
    this.input.emit(event);
  }
}
